import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { DossierOverview } from '../../types';
import { getDossierOverview, initializeDossier } from '../../services/dossier.service';
import AchizitiiSection from './achizitii/AchizitiiSection';
import ContracteSection from './contracte/ContracteSection';
import LivrabileSection from './livrabile/LivrabileSection';
import ImplementareSection from './implementare/ImplementareSection';
import ActivitatiSection from './ActivitatiSection';

interface Props {
  projectId: number;
  section: 'achizitii' | 'contracte' | 'livrabile' | 'implementare' | 'activitati';
  // Notifică pagina-părinte să reîncarce sumarul (badge-uri taburi, „De făcut") după un CRUD.
  onOverviewChange?: () => void;
}

export default function DossierTab({ projectId, section, onOverviewChange }: Props) {
  const [overview, setOverview] = useState<DossierOverview | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const autoInitRef = useRef(false);

  const loadOverview = useCallback(async () => {
    try {
      const data = await getDossierOverview(projectId);
      setOverview(data);
      return data;
    } catch (err) {
      console.error('Failed to load dossier:', err);
      return null;
    } finally {
      setInitialLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    (async () => {
      const data = await loadOverview();
      if (data && !data.exists && !autoInitRef.current) {
        autoInitRef.current = true;
        try {
          await initializeDossier(projectId);
          await loadOverview();
        } catch (err) {
          console.error('Auto-initialize dossier failed:', err);
        }
      }
    })();
  }, [loadOverview, projectId]);

  if (initialLoading || !overview?.exists) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  const dossierId = overview.dossier!.id;

  const getStatusColor = (module: string): 'green' | 'yellow' | 'gray' | 'red' => {
    if (module === 'achizitii') {
      if (overview.achizitii.total === 0) return 'gray';
      if (overview.achizitii.completed === overview.achizitii.total) return 'green';
      if (overview.achizitii.inProgress > 0) return 'yellow';
      return 'gray';
    }
    if (module === 'contracte') {
      if (overview.contracte.total === 0) return 'gray';
      if (overview.contracte.remainingValue < 0) return 'red';
      return 'green';
    }
    if (module === 'livrabile') {
      if (overview.livrabile.total === 0) return 'gray';
      if (overview.livrabile.approved === overview.livrabile.total) return 'green';
      if (overview.livrabile.approved > 0 || overview.livrabile.completed > 0 || overview.livrabile.inProgress > 0) return 'yellow';
      return 'gray';
    }
    if (module === 'implementare') {
      if (overview.implementare.totalEvents === 0) return 'gray';
      return 'yellow';
    }
    return 'gray';
  };

  // După un CRUD într-o secțiune: reîncarcă sumarul local ȘI notifică pagina-părinte,
  // ca badge-urile taburilor și „De făcut" să nu rămână cu cifre învechite.
  const handleRefresh = async () => {
    await loadOverview();
    onOverviewChange?.();
  };

  if (section === 'achizitii') {
    return (
      <AchizitiiSection
        dossierId={dossierId}
        projectId={projectId}
        summary={overview.achizitii}
        statusColor={getStatusColor('achizitii')}
        onRefresh={handleRefresh}
      />
    );
  }

  if (section === 'contracte') {
    return (
      <ContracteSection
        dossierId={dossierId}
        projectId={projectId}
        summary={overview.contracte}
        statusColor={getStatusColor('contracte')}
        onRefresh={handleRefresh}
      />
    );
  }

  if (section === 'livrabile') {
    return (
      <LivrabileSection
        dossierId={dossierId}
        projectId={projectId}
        summary={overview.livrabile}
        statusColor={getStatusColor('livrabile')}
        onRefresh={handleRefresh}
      />
    );
  }

  if (section === 'implementare') {
    return (
      <ImplementareSection
        dossierId={dossierId}
        projectId={projectId}
        summary={overview.implementare}
        onRefresh={handleRefresh}
      />
    );
  }

  if (section === 'activitati') {
    return (
      <ActivitatiSection
        dossierId={dossierId}
        projectId={projectId}
        onRefresh={handleRefresh}
      />
    );
  }

  return null;
}
